'use client';

import { c as _c } from "react/compiler-runtime";
import { Content as SelectContentPrimitive, Group as SelectGroupPrimitive, Icon as SelectIconPrimitive, ItemIndicator as SelectItemIndicatorPrimitive, Item as SelectItemPrimitive, ItemText as SelectItemTextPrimitive, Label as SelectLabelPrimitive, Portal as SelectPortalPrimitive, Root as SelectPrimitive, ScrollDownButton as SelectScrollDownButtonPrimitive, ScrollUpButton as SelectScrollUpButtonPrimitive, Separator as SelectSeparatorPrimitive, Trigger as SelectTriggerPrimitive, Value as SelectValuePrimitive, Viewport as SelectViewportPrimitive } from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import type { ComponentPropsWithoutRef, ComponentRef, Ref } from 'react';
import { getOverlayPortalRoot } from '@/lib/dom/overlay-root';
import { cn } from '@/lib/utils';
const Select = SelectPrimitive;
const SelectGroup = SelectGroupPrimitive;
const SelectValue = SelectValuePrimitive;
type SelectTriggerProperties = ComponentPropsWithoutRef<typeof SelectTriggerPrimitive> & {
  ref?: Ref<ComponentRef<typeof SelectTriggerPrimitive>> | undefined;
};
const SelectTrigger = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let properties;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = properties;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    properties = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-border/60 bg-card/80 px-3 py-2 text-sm shadow-sm ring-offset-background backdrop-blur-sm supports-[backdrop-filter]:bg-background/55 focus:outline-none focus:ring-1 focus:ring-ring data-[placeholder]:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectIconPrimitive asChild={true}><ChevronDown className="h-4 w-4 opacity-50" /></SelectIconPrimitive>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== properties || $[10] !== ref || $[11] !== t1) {
    t3 = <SelectTriggerPrimitive ref={ref} className={t1} {...properties}>{children}{t2}</SelectTriggerPrimitive>;
    $[8] = children;
    $[9] = properties;
    $[10] = ref;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
type SelectScrollUpButtonProperties = ComponentPropsWithoutRef<typeof SelectScrollUpButtonPrimitive> & {
  ref?: Ref<ComponentRef<typeof SelectScrollUpButtonPrimitive>> | undefined;
};
const SelectScrollUpButton = t0 => {
  const $ = _c(11);
  let className;
  let properties;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
    $[3] = ref;
  } else {
    className = $[1];
    properties = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex cursor-default items-center justify-center py-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUp className="h-4 w-4" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== properties || $[8] !== ref || $[9] !== t1) {
    t3 = <SelectScrollUpButtonPrimitive ref={ref} className={t1} {...properties}>{t2}</SelectScrollUpButtonPrimitive>;
    $[7] = properties;
    $[8] = ref;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
type SelectScrollDownButtonProperties = ComponentPropsWithoutRef<typeof SelectScrollDownButtonPrimitive> & {
  ref?: Ref<ComponentRef<typeof SelectScrollDownButtonPrimitive>> | undefined;
};
const SelectScrollDownButton = t0 => {
  const $ = _c(11);
  let className;
  let properties;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
    $[3] = ref;
  } else {
    className = $[1];
    properties = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex cursor-default items-center justify-center py-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDown className="h-4 w-4" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== properties || $[8] !== ref || $[9] !== t1) {
    t3 = <SelectScrollDownButtonPrimitive ref={ref} className={t1} {...properties}>{t2}</SelectScrollDownButtonPrimitive>;
    $[7] = properties;
    $[8] = ref;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
type SelectContentProperties = ComponentPropsWithoutRef<typeof SelectContentPrimitive> & {
  position?: 'popper' | 'item-aligned';
  ref?: Ref<ComponentRef<typeof SelectContentPrimitive>> | undefined;
};
const SelectContent = t0 => {
  const $ = _c(23);
  let children;
  let className;
  let properties;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = properties;
    $[4] = ref;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    properties = $[3];
    ref = $[4];
    t1 = $[5];
  }
  const position = t1 === undefined ? "popper" : t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getOverlayPortalRoot() ?? undefined;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1";
  let t4;
  if ($[7] !== className || $[8] !== t3) {
    t4 = cn("relative z-layer-toast max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border border-border/60 bg-card/80 text-popover-foreground shadow-lg backdrop-blur-md supports-[backdrop-filter]:bg-background/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]", t3, className);
    $[7] = className;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SelectScrollUpButton />;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]";
  let t7;
  if ($[11] !== t6) {
    t7 = cn("p-1", t6);
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== children || $[14] !== t7) {
    t8 = <SelectViewportPrimitive className={t7}>{children}</SelectViewportPrimitive>;
    $[13] = children;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SelectScrollDownButton />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== position || $[18] !== properties || $[19] !== ref || $[20] !== t4 || $[21] !== t8) {
    t10 = <SelectPortalPrimitive container={t2}><SelectContentPrimitive ref={ref} className={t4} position={position} {...properties}>{t5}{t8}{t9}</SelectContentPrimitive></SelectPortalPrimitive>;
    $[17] = position;
    $[18] = properties;
    $[19] = ref;
    $[20] = t4;
    $[21] = t8;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
type SelectLabelProperties = ComponentPropsWithoutRef<typeof SelectLabelPrimitive> & {
  ref?: Ref<ComponentRef<typeof SelectLabelPrimitive>> | undefined;
};
const SelectLabel = t0 => {
  const $ = _c(10);
  let className;
  let properties;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
    $[3] = ref;
  } else {
    className = $[1];
    properties = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("px-2 py-1.5 text-sm font-semibold", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== properties || $[7] !== ref || $[8] !== t1) {
    t2 = <SelectLabelPrimitive ref={ref} className={t1} {...properties} />;
    $[6] = properties;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
type SelectItemProperties = ComponentPropsWithoutRef<typeof SelectItemPrimitive> & {
  ref?: Ref<ComponentRef<typeof SelectItemPrimitive>> | undefined;
};
const SelectItem = t0 => {
  const $ = _c(15);
  let children;
  let className;
  let properties;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = properties;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    properties = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center"><SelectItemIndicatorPrimitive><Check className="h-4 w-4" /></SelectItemIndicatorPrimitive></span>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children) {
    t3 = <SelectItemTextPrimitive>{children}</SelectItemTextPrimitive>;
    $[8] = children;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== properties || $[11] !== ref || $[12] !== t1 || $[13] !== t3) {
    t4 = <SelectItemPrimitive ref={ref} className={t1} {...properties}>{t2}{t3}</SelectItemPrimitive>;
    $[10] = properties;
    $[11] = ref;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
type SelectSeparatorProperties = ComponentPropsWithoutRef<typeof SelectSeparatorPrimitive> & {
  ref?: Ref<ComponentRef<typeof SelectSeparatorPrimitive>> | undefined;
};
const SelectSeparator = t0 => {
  const $ = _c(10);
  let className;
  let properties;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
    $[3] = ref;
  } else {
    className = $[1];
    properties = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-muted", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== properties || $[7] !== ref || $[8] !== t1) {
    t2 = <SelectSeparatorPrimitive ref={ref} className={t1} {...properties} />;
    $[6] = properties;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem, SelectSeparator, SelectScrollUpButton, SelectScrollDownButton };